import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loescheKategorie, selectKategorien } from 'modules/finanzen/features/finanzenSlice';
import { TYP_AUSGABE, TYP_EINNAHME } from 'modules/finanzen/features/konstanten';

/** Eine Zeile mit eigener Kategorie und Löschen-Knopf. */
function EigeneZeile({ kategorie, onLoeschen }) {
  return (
    <li className="flex items-center justify-between gap-2 px-1 py-2">
      <span className="text-ink">
        {kategorie.icon} {kategorie.name}
      </span>
      <button
        type="button"
        onClick={onLoeschen}
        className="rounded-lg border border-red-200 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-50"
      >
        Löschen
      </button>
    </li>
  );
}

/** Gruppe eigener Kategorien eines Typs. */
function Gruppe({ titel, kategorien, onLoeschen }) {
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-ink-muted">{titel}</h3>
      {kategorien.length === 0 ? (
        <p className="px-1 py-2 text-sm text-ink-muted">Keine eigenen Kategorien.</p>
      ) : (
        <ul className="divide-y divide-line">
          {kategorien.map((k) => (
            <EigeneZeile key={k.uuid} kategorie={k} onLoeschen={() => onLoeschen(k)} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Modal zum Verwalten der eigenen Kategorien: Überblick und Löschen. Neue
 * Kategorien werden direkt beim Erfassen angelegt (siehe KategorieAuswahl).
 * Standard-Kategorien sind nicht löschbar und erscheinen hier nicht.
 */
export default function KategorieManager({ onClose }) {
  const dispatch = useDispatch();
  const eigene = useSelector(selectKategorien);
  const [laeuft, setLaeuft] = useState(false);

  const ausgaben = eigene.filter((k) => k.typ === TYP_AUSGABE);
  const einnahmen = eigene.filter((k) => k.typ === TYP_EINNAHME);

  const entfernen = async (k) => {
    if (!window.confirm(`Kategorie „${k.name}“ löschen? Bereits erfasste Buchungen bleiben unverändert.`)) return;
    setLaeuft(true);
    try {
      await dispatch(loescheKategorie(k.uuid)).unwrap();
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-paper p-6 shadow-xl">
        <h2 className="mb-1 font-serif text-xl font-semibold text-ink">Kategorien verwalten</h2>
        <p className="mb-4 text-sm text-ink-muted">
          Neue Kategorien legst du direkt beim Erfassen an („＋ Neu“). Hier kannst du deine eigenen wieder
          entfernen.
        </p>

        <div className="space-y-4">
          <Gruppe titel="Ausgaben" kategorien={ausgaben} onLoeschen={entfernen} />
          <Gruppe titel="Einnahmen" kategorien={einnahmen} onLoeschen={entfernen} />
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={laeuft}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
          >
            Fertig
          </button>
        </div>
      </div>
    </div>
  );
}
